import { useTranslation } from 'react-i18next';
import store from '../../../../store/store';
import { CodeSection } from 'react-code-section-lib';
import {
  calenderSVG,
  clockSVG,
  connectSVG,
} from '../../../../lib/allCustomSVGs';
import { useEffect, useState } from 'react';
import {
  regularScriptCode,
  shopifyPixelCode,
  shopifyPixelName,
} from '../../../../lib/constants/integration_script_code';
import AISelectInput from '../../../../ui/inputs/AISelectInput';
import '../../../../styles/pages/checkoutTracking/trackingIntegration.scss';
import StatusCard from '../../../../ui/cards/StatusCard';
import makeFirstLetterCapital from '../../../../lib/helper-functions/makeFirstLetterCapital';
import isNotEmptyString from '../../../../lib/helper-functions/isNotEmptyString';
import {
  APP_ENV,
  CHECK_CONNECTION_API,
  SHOPIFY_AUTH_URL,
  shopifyAdminURL,
} from '../../../../lib/constants/central-api';
import ValueSelector from '../../../../ui/inputs/ValueSelector';
import shopifyButtonImg from '../../../../assets/shopify.png';
import { injectShopifyHeadScript } from './injectShopifyHeadScript';
import { applicationWWWXRLEncodedBodyBuilder } from '../../../../lib/utils/body-builder';
import { asyncHandler } from '../../../../lib/api/async-handler';
import { RequestInfo } from '../../../../interfaces/RequestInfo';
import LoadingSpinner from '../../../../ui/loadingSpinner/LoadingSpinner';
import { setMessage } from '../../../../store/reducers/appSlice';
import MainSquareButton from '../../../../ui/buttons/MainSquareButton';
import {
  addSelectedCMS,
  setIsTrackingScriptConnectionSuccess,
  setUserCompanyDetails,
} from '../../../../store/reducers/userSlice';
import AIURLInput from '../../../../ui/inputs/AIURLInput';
import post_ai_tracker_basic_configs_api_call from '../../../../lib/api/integration/post_ai_tracker_basic_configs_api_call';
import ToggleSwitch from '../../../../ui/buttons/ToggleSwitch';
import removeHttpPrefix from '../../../../lib/helper-functions/removeHttpPrefix';
interface Props {
  apiKey: any;
  isTrackerON: boolean;
  setIsTrackerON: (value: boolean) => void;
  isTrackingScriptConnected: boolean;
  setIsTrackingScriptConnected: (value: boolean) => void;
  optimizeValue: number;
  setOptimizeValue: (value: number) => void;
  isAutofillEnabled: boolean;
  data: any;
  setAPIData: (data: any) => void;
  setScriptKey: (key: string) => void;
  isLoadingConnection: boolean;
  setIsLoadingConnection: (value: boolean) => void;
}

function Integration({
  apiKey,
  isTrackerON,
  setIsTrackerON,
  isTrackingScriptConnected,
  setIsTrackingScriptConnected,
  optimizeValue,
  setOptimizeValue,
  isAutofillEnabled,
  data,
  setAPIData,
  setScriptKey,
  isLoadingConnection,
  setIsLoadingConnection,
}: Props) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInstalling, setIsInstalling] = useState<boolean>(false);
  const [trackerSwitchToggleState, setTrackerSwitchToggleState] =
    useState<boolean>(isTrackerON);
  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('pending');
  const url_prefix = 'https://';

  const [shopURL, setShopURL] = useState<string>(
    removeHttpPrefix(data?.shop?.webshop_url ?? '')
  );

  const [detectedCMS, setDetectedCMS] = useState(
    isNotEmptyString(data?.additional_configs?.CMS_type)
      ? data?.additional_configs?.CMS_type
      : 'custom'
  );

  const percentagesArr = Array.from({ length: 10 }, (_, index) => {
    return {
      label: (index + 1)?.toString(),
      value: (index + 1) * 10,
    };
  });

  const detectCMS_Arr = [];

  const preDefinedCMS_Arr_without_shopify = [
    { OptionValue: 'custom', OptionName: 'Custom' },
  ];

  const preDefinedCMS_Arr = [
    { OptionValue: 'shopify', OptionName: 'Shopify' },
    ...preDefinedCMS_Arr_without_shopify,
  ];

  const cmsType = data?.additional_configs?.CMS_type || '';

  if (isNotEmptyString(cmsType) && cmsType !== 'custom') {
    detectCMS_Arr.push({
      OptionValue: cmsType,
      OptionName: `${makeFirstLetterCapital(cmsType)} (Auto-detected)`,
    });

    const cmsArray =
      cmsType === 'shopify'
        ? preDefinedCMS_Arr_without_shopify
        : preDefinedCMS_Arr;
    detectCMS_Arr.push(...cmsArray);
  } else {
    detectCMS_Arr.push(...preDefinedCMS_Arr);
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.href.split('?')[1]);
    const code: any = params.get('code');
    const shop: any = params.get('shop');

    if (isNotEmptyString(code) && isNotEmptyString(shop)) {
      //* Removing params from browser URL and continue with the authentication process
      window.history.replaceState({}, '', `${window?.location?.pathname}`);
      injectShopifyHeadScript(
        code,
        shop,
        data?.shop?.id,
        apiKey,
        setIsInstalling
      );
    }
  }, []);

  useEffect(() => {
    if (isTrackerON) {
      checkConnection();
    } else {
      setLoading('success');
    }
  }, []);

  useEffect(() => {
    if (!isTrackerON) {
      setIsTrackingScriptConnected(false);
      setIsLoadingConnection(false);
      store.dispatch(setIsTrackingScriptConnectionSuccess('false'));
    }
  }, [isTrackerON]);

  const saveSetup = async () => {
    if (!apiKey || !shopURL || !isNotEmptyString(shopURL)) {
      if (!isNotEmptyString(shopURL)) {
        store.dispatch(
          setMessage({
            message: t('BasicSetup-error-msg-shop-url'),
            messageType: 'warning',
          })
        );
      }
      return;
    }
    try {
      setIsLoading(true);
      const res = await post_ai_tracker_basic_configs_api_call(
        `${url_prefix}${shopURL}`,
        detectedCMS,
        optimizeValue,
        trackerSwitchToggleState
      );
      if (res?.success) {
        if (trackerSwitchToggleState) {
          await checkConnection(true);
        } else {
          store.dispatch(
            setMessage({
              message: t('BasicSetup-success-msg-with-tracking-disabled'),
              messageType: 'success',
            })
          );
        }
        setIsTrackerON(trackerSwitchToggleState);
        store.dispatch(addSelectedCMS(detectedCMS));
        store.dispatch(
          setUserCompanyDetails({
            webshop_url: `${url_prefix}${shopURL}`,
          })
        );
      } else {
        store.dispatch(
          setMessage({
            message: t('BasicSetup-error-msg'),
            messageType: 'error',
          })
        );
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setIsLoading(false);
      setIsLoadingConnection(false);
      store.dispatch(
        setMessage({
          message: t('BasicSetup-error-msg'),
          messageType: 'error',
        })
      );
    }
  };

  const checkConnection = async (
    shouldShowMsg?: boolean,
    manualCheck?: boolean
  ) => {
    setIsLoadingConnection(true);
    if (!apiKey || !shopURL || shopURL === '') {
      setIsTrackingScriptConnected(false);
      setLoading('success');
      setIsLoadingConnection(false);
      store.dispatch(setIsTrackingScriptConnectionSuccess('false'));
      return;
    }

    try {
      const sendingData = {
        tracking_id: apiKey,
        site_url: `${url_prefix}${shopURL}`,
      };

      const requestInfo: RequestInfo = {
        url: CHECK_CONNECTION_API,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
      };

      const actionType = 'CHECK_CONNECTION_API';
      const customMsg = '';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        false,
        false,
        false,
        [],
        true
      );

      const response = res.data;

      if (
        res.actionType === actionType &&
        response?.success === true &&
        response?.data?.script_detected
      ) {
        setIsTrackingScriptConnected(true);
        store.dispatch(setIsTrackingScriptConnectionSuccess('true'));
        if (shouldShowMsg) {
          store.dispatch(
            setMessage({
              message: t('BasicSetup-success-msg-with-tracking-enabled'),
              messageType: 'success',
            })
          );
        }
        if (manualCheck) {
          store.dispatch(
            setMessage({
              message: t('Tracking-Script-Connected-msg'),
              messageType: 'success',
            })
          );
        }
      } else {
        setIsTrackingScriptConnected(false);
        store.dispatch(setIsTrackingScriptConnectionSuccess('false'));
        if (shouldShowMsg) {
          store.dispatch(
            setMessage({
              message: t('BasicSetup-success-msg-with-tracking-pending'),
              messageType: 'warning',
            })
          );
        }
        if (manualCheck) {
          store.dispatch(
            setMessage({
              message: t('Tracking-Script-Not-Connected-msg'),
              messageType: 'warning',
            })
          );
        }
      }
      setLoading('success');
      setIsLoadingConnection(false);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setIsTrackingScriptConnected(false);
      store.dispatch(setIsTrackingScriptConnectionSuccess('false'));
      setLoading('success');
      setIsLoadingConnection(false);
    }
    return;
  };

  const renderIntegrationCode = () => {
    return (
      <div
        style={{ paddingTop: 0 }}
        className={`normalForm ${!isTrackerON ? 'disabled' : ''}`}
      >
        <div className='trackingIntegration--titleWithSwitch'>
          <div className='trackingIntegration--titleWithSwitch-title'>
            <h1 className='trackingIntegration-title'>
              {t('IntegrationSetup')}
            </h1>
            <span className='trackingIntegration-subtitle'>
              {t('FollowTheseStepsToIntegrateTrackingOnYourWebsite')}
            </span>
          </div>
          <div className='trackingIntegration--titleWithSwitch-switch'>
            <MainSquareButton
              loading={isLoadingConnection}
              onClick={(e) => {
                e.preventDefault();
                checkConnection(false, true);
              }}
              type={'button'}
              value={t('CheckConnection')}
              normal={true}
            />
          </div>
        </div>
        <div style={{ marginTop: '2rem' }} className='normalForm__top'>
          {/* //* ----------- RegularIntegration -- Start ----------- */}
          {detectedCMS !== 'shopify' && (
            <>
              <span className='trackingIntegration-subtitle-2'>
                {t('CheckoutTrackingIntegrationInstructions1')}
              </span>
              <div className='trackingIntegration-subtitle'>
                {t('CheckoutTrackingIntegrationInstructions2')} {`<head>`}
              </div>

              <div className='code__snippet'>
                <CodeSection
                  showLineNumbers={true}
                  theme={'dark'}
                  lang={'html'}
                >
                  {regularScriptCode(apiKey)}
                </CodeSection>
              </div>
            </>
          )}
          {/* //* ----------- RegularIntegration -- End ----------- */}

          {/* //* ----------- ShopifyIntegration -- Start ----------- */}
          {detectedCMS === 'shopify' && (
            <>
              <span className='trackingIntegration-subtitle-2'>
                {t(
                  APP_ENV !== 'LIVE'
                    ? 'CheckoutTrackingIntegrationInstructions0-Shopify-New'
                    : 'CheckoutTrackingIntegrationInstructions0-Shopify'
                )}
              </span>

              {APP_ENV !== 'LIVE' && (
                <>
                  <div className='trackingIntegration-subtitle'>
                    {t('CheckoutTrackingIntegrationInstructions8-Shopify')}
                  </div>
                  <div
                    className='normalForm__container1--left'
                    style={{ marginBottom: '1.8rem', maxWidth: '32.3rem' }}
                  >
                    <div className='login__right--other'>
                      <div className='login__right--bottom-button'>
                        <MainSquareButton
                          value={
                            isInstalling
                              ? `${t('Installing')}...`
                              : `${t(
                                  'CheckoutTrackingIntegrationInstructions9-Shopify'
                                )}`
                          }
                          image={shopifyButtonImg}
                          type='button'
                          disabled={isInstalling}
                          onClick={() => {
                            setIsInstalling(true);
                            window.location.href = SHOPIFY_AUTH_URL(
                              `${url_prefix}${shopURL}`,
                              `${window.location.origin}/tracking/integration`
                            );
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div
                style={{ marginBottom: '1rem' }}
                className='trackingIntegration-subtitle'
              >
                {t('CheckoutTrackingIntegrationInstructions1-Shopify')}
              </div>
              <ul className='trackingIntegration-list'>
                <li>
                  {t('CheckoutTrackingIntegrationInstructions2-Shopify')} ({' '}
                  <a
                    onClick={() => {
                      window.open(shopifyAdminURL, '_blank');
                    }}
                  >
                    {shopifyAdminURL}
                  </a>
                  )
                </li>
                <li>{t('CheckoutTrackingIntegrationInstructions3-Shopify')}</li>
                <li>{t('CheckoutTrackingIntegrationInstructions4-Shopify')}</li>
                <li>{t('CheckoutTrackingIntegrationInstructions5-Shopify')}</li>
                <div
                  className='code__snippet__single-line'
                  style={{
                    width: '25rem',
                    marginTop: '-4rem',
                    marginBottom: '-1rem',
                    marginLeft: '1.5rem',
                  }}
                >
                  <CodeSection
                    showLineNumbers={false}
                    theme={'dark'}
                    lang={'html'}
                  >
                    {shopifyPixelName(apiKey)}
                  </CodeSection>
                </div>
                <li>{t('CheckoutTrackingIntegrationInstructions6-Shopify')}</li>
                <li>{t('CheckoutTrackingIntegrationInstructions7-Shopify')}</li>
              </ul>
              <div className='code__snippet'>
                <CodeSection
                  showLineNumbers={true}
                  theme={'dark'}
                  lang={'html'}
                >
                  {shopifyPixelCode(apiKey)}
                </CodeSection>
              </div>
            </>
          )}
          {/* //* ----------- ShopifyIntegration -- End ----------- */}
        </div>
      </div>
    );
  };

  if (loading === 'pending') {
    return <LoadingSpinner />;
  }

  return (
    <div className='trackingIntegration'>
      {/* ======================================================================= */}
      <div className='trackingIntegration-wrapper'>
        <h1 className='trackingIntegration-title'>{t('IntegrationStatus')}</h1>
        <span className='trackingIntegration-subtitle'>
          {t('OverviewOfYourIntegrationPerformance')}.
        </span>
        <div className='trackingIntegration-status'>
          <StatusCard
            title={t('LastRequest')}
            info={data?.integration_stats?.recent_request_age ?? 'N/A'}
            description={t('LastTrackingRequestReceived')}
            icon={clockSVG}
          />
          <StatusCard
            title={t('IntegrationAge')}
            info={
              data?.integration_stats?.age
                ? data?.integration_stats?.age?.replace(' ago', '')
                : 'N/A'
            }
            description={t('SinceFirstIntegration')}
            icon={clockSVG}
          />
          <StatusCard
            title={t('RequestsToday')}
            info={data?.integration_stats?.today_request_counts ?? '0'}
            description={t('TrackingRequestsReceivedToday')}
            icon={calenderSVG}
          />
          <StatusCard
            title={t('Status')}
            info={
              isTrackerON
                ? isTrackingScriptConnected
                  ? t('Active')
                  : t('Pending')
                : t('Inactive')
            }
            description={
              isTrackerON
                ? isTrackingScriptConnected
                  ? t('IntegrationIsWorkingProperly')
                  : t('IntegrationPending')
                : t('IntegrationIsPaused')
            }
            color={
              isTrackerON
                ? isTrackingScriptConnected
                  ? 'green'
                  : 'yellow'
                : 'red'
            }
            icon={connectSVG}
          />
        </div>
      </div>
      {/* ======================================================================= */}
      <div
        style={{ marginTop: '2rem' }}
        className='trackingIntegration-wrapper'
      >
        <div style={{ paddingTop: 0 }}>
          <div className='trackingIntegration--titleWithSwitch'>
            <div className='trackingIntegration--titleWithSwitch-title'>
              <h1 className='trackingIntegration-title'>{t('BasicSetup')}</h1>
              <span className='trackingIntegration-subtitle'>
                {t('ConfigureYourWebshopIntegration')}
              </span>
            </div>

            <div className='trackingIntegration--titleWithSwitch-switch'>
              <ToggleSwitch
                isOn={trackerSwitchToggleState}
                title={t('EnableTracking')}
                onClick={() => {
                  setTrackerSwitchToggleState(!trackerSwitchToggleState);
                }}
                disabled={isLoading}
                id='Integration-TrackerSwitchToggle'
              />
            </div>
          </div>

          <div className='normalForm__top'>
            <div
              className='normalForm__container1'
              style={{ cursor: 'auto', marginBottom: '1rem' }}
            >
              <div className='normalForm__container1--left'>
                <AIURLInput
                  id='integration__webshop-url'
                  value={shopURL}
                  label={t('WebsiteURL')}
                  placeholder={t('your-website.com')}
                  changeListeners={[
                    (e) => {
                      setShopURL(removeHttpPrefix(e.target.value));
                    },
                  ]}
                  prefix={url_prefix}
                  title={t('URL-validation')}
                  readOnly={isLoading}
                  required={true}
                />
                <br />
              </div>

              <div className='normalForm__container1--right'>
                <AISelectInput
                  defaultValue={detectedCMS}
                  setSelect={setDetectedCMS}
                  selectOptionArr={detectCMS_Arr}
                  placeholder={t('CMSType')}
                  id={'integration__detect-cms'}
                  disabled={isLoading}
                />
                <br />
              </div>
            </div>

            <span className='trackingIntegration-subtitle-2'>
              {t('OptimizationRatio')}
            </span>
            <div className='trackingIntegration-subtitle'>
              {t('SelectHowManyUsersShouldBeOptimized')}
            </div>
            <div className='optimization-ratio-container'>
              <div className='value-selector-wrapper'>
                <ValueSelector
                  onChange={setOptimizeValue}
                  value={optimizeValue}
                  loading={isLoading}
                  isEqualSignVisible={true}
                  symbol={'%'}
                  content={percentagesArr}
                />
              </div>
              <div className='save-button-wrapper'>
                <MainSquareButton
                  loading={isLoading}
                  onClick={(e) => {
                    e.preventDefault();
                    saveSetup();
                  }}
                  type={'button'}
                  value={t('UpdateSetup')}
                  normal={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* ======================================================================= */}
      {
        <div
          style={{
            marginTop: '2rem',
            cursor: !isTrackerON ? 'not-allowed' : 'auto',
          }}
          className='trackingIntegration-wrapper'
        >
          {renderIntegrationCode()}
        </div>
      }
      {/* ======================================================================= */}
    </div>
  );
}

export default Integration;
